import React, { useState, useEffect, useRef } from 'react';
import { Order, OrderItem } from '../types';
import { X, Save, Plus, Minus, Trash2, Package } from 'lucide-react';
import {
  getOrderItemDisplayName,
  getOrderItemOptionLabel,
} from '../utils/orderItemDisplay';


interface EditOrderBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
  onSave: (updatedItems: OrderItem[]) => void;
  onLoadLatestOrder?: () => Promise<Order | null>;
  orderLabel?: string;
}

const sanitizeImageUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const isManualOrderItem = (item: OrderItem) => !item.productId || item.isCustom;

const EditOrderBottomSheet: React.FC<EditOrderBottomSheetProps> = ({
  isOpen,
  onClose,
  order,
  onSave,
  onLoadLatestOrder,
  orderLabel,
}) => {
  const [localItems, setLocalItems] = useState<OrderItem[]>([]);
  const [isLoadingLatestOrder, setIsLoadingLatestOrder] = useState(false);
  const [latestOrderError, setLatestOrderError] = useState<string | null>(null);
  const loadedOrderIdRef = useRef<string | null>(null);
  const onLoadLatestOrderRef = useRef(onLoadLatestOrder);

  const isEditable = !['COMPLETED', 'CANCELLED'].includes(order.status);

  useEffect(() => {
    onLoadLatestOrderRef.current = onLoadLatestOrder;
  }, [onLoadLatestOrder]);

  useEffect(() => {
    if (isOpen) {
      setLocalItems(order.items.map(item => ({ ...item })));
    }
  }, [isOpen, order]);

  useEffect(() => {
    if (!isOpen) {
      loadedOrderIdRef.current = null;
      setIsLoadingLatestOrder(false);
      setLatestOrderError(null);
      return;
    }

    const loadLatestOrder = onLoadLatestOrderRef.current;
    if (!loadLatestOrder || loadedOrderIdRef.current === order.id) {
      return;
    }

    loadedOrderIdRef.current = order.id;
    setIsLoadingLatestOrder(true);
    setLatestOrderError(null);

    void (async () => {
      try {
        const latestOrder = await loadLatestOrder();
        const isCurrentOrderLoad = loadedOrderIdRef.current === order.id;
        if (!isCurrentOrderLoad || !latestOrder) {
          return;
        }

        setLocalItems(latestOrder.items.map((item) => ({ ...item })));
      } catch (error) {
        if (loadedOrderIdRef.current !== order.id) {
          return;
        }

        console.error('Failed to fetch latest order for editing', error);
        setLatestOrderError('최신 주문 정보를 불러오지 못해 현재 화면 데이터로 편집합니다.');
      } finally {
        const shouldFinishLoading = loadedOrderIdRef.current === order.id;
        if (shouldFinishLoading) {
          setIsLoadingLatestOrder(false);
        }
      }
    })();

  }, [isOpen, order.id]);

  if (!isOpen) return null;

  const handleUpdateQty = (itemId: string, delta: number) => {
    setLocalItems(prev => prev.map(item => {
      if (item.id === itemId) {
        if (
          isManualOrderItem(item) ||
          (delta < 0 && item.isCustomerCancellationEnabled === false)
        ) {
          return item;
        }

        const newQty = item.quantity + delta;
        if (newQty <= 0) {
          return { ...item, quantity: 0 };
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const handleRemove = (itemId: string) => {
    const targetItem = localItems.find((item) => item.id === itemId);
    if (
      targetItem &&
      (isManualOrderItem(targetItem) ||
        targetItem.isCustomerCancellationEnabled === false)
    ) {
      return;
    }

    setLocalItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, quantity: 0 } : item
    ));
  };

  const totalPrice = localItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <div className="fixed inset-0 z-[2000] flex items-end justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      
      {/* Sheet Content */}
      <div className="relative w-full max-w-lg bg-white rounded-t-[32px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mt-4 mb-2" />
        
        <div className="px-6 py-4 flex items-center justify-between border-b">
          <div>
            <h2 className="text-xl font-black text-gray-900">주문 수정</h2>
            <p className="text-xs text-gray-400 font-bold mt-0.5 uppercase tracking-tighter">
              {orderLabel || order.id}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto px-6 py-6 space-y-4">
          {(isLoadingLatestOrder || latestOrderError) && (
            <div
              className={`rounded-2xl border px-4 py-3 text-[11px] font-bold ${
                latestOrderError
                  ? 'border-orange-100 bg-orange-50 text-orange-600'
                  : 'border-emerald-100 bg-emerald-50 text-emerald-700'
              }`}
            >
              {latestOrderError || '최신 주문 정보를 불러오는 중입니다.'}
            </div>
          )}
          {localItems.length === 0 ? (
            <div className="py-20 text-center text-gray-400">
              <p className="text-sm font-bold">수정할 상품이 없습니다.</p>
            </div>
          ) : (
            localItems.map((item) => {
              const displayName = getOrderItemDisplayName(item);
              const optionLabel = getOrderItemOptionLabel(item);
              const imageUrl = sanitizeImageUrl(item.image);
              const isManualItem = isManualOrderItem(item);
              const isCancellationLocked =
                item.quantity > 0 &&
                item.isCustomerCancellationEnabled === false;

              return (
                <div key={item.id ?? item.productId} className={`flex items-start gap-4 p-4 rounded-[20px] border transition-opacity ${item.quantity === 0 ? 'bg-gray-100/50 border-gray-200 opacity-60' : 'bg-gray-50 border-gray-100'}`}>
                  {imageUrl ? (
                    <img src={imageUrl} className={`w-14 h-14 rounded-xl object-cover border bg-white mt-0.5 ${item.quantity === 0 ? 'grayscale' : ''}`} alt={displayName} />
                  ) : (
                    <div className="w-14 h-14 rounded-xl border bg-white mt-0.5 shrink-0 flex items-center justify-center text-gray-300">
                      <Package size={18} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-start gap-2">
                          <h3 className={`text-sm font-bold line-clamp-2 break-words leading-tight min-h-[2.25rem] ${item.quantity === 0 ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                            {displayName}
                          </h3>
                          {isManualItem && (
                            <span className="shrink-0 text-[9px] font-black bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-md">
                              판매자 추가
                            </span>
                          )}
                          {isCancellationLocked && (
                            <span className="shrink-0 text-[9px] font-black bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-md">
                              취소 불가
                            </span>
                          )}
                        </div>
                        {optionLabel && (
                          <p className={`mt-1 text-[10px] font-black ${item.quantity === 0 ? 'text-gray-300' : 'text-emerald-600'}`}>
                            옵션: {optionLabel}
                          </p>
                        )}
                        {isManualItem && (
                          <p className="mt-1 text-[10px] font-bold text-amber-600">
                            이 품목은 판매자만 수정할 수 있습니다.
                          </p>
                        )}
                        {isCancellationLocked && (
                          <p className="mt-1 text-[10px] font-bold text-orange-600">
                            주문 후에는 수량을 줄이거나 상품을 취소할 수 없습니다.
                          </p>
                        )}
                      </div>
                      {item.quantity === 0 && (
                        <span className="shrink-0 text-[10px] font-black bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-md">취소됨</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-2.5">
                      <div>
                        <p className={`text-sm font-black ${item.quantity === 0 ? 'text-gray-400' : 'text-emerald-600'}`}>
                          {item.price.toLocaleString()}원
                        </p>
                        <p className="text-[10px] font-bold text-gray-400">
                          수량 {item.quantity}개
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex items-center bg-white rounded-full border border-gray-200 h-8 px-1">
                          <button
                            onClick={() => handleUpdateQty(item.id!, -1)}
                            disabled={item.quantity === 0 || !isEditable || isLoadingLatestOrder || isManualItem || isCancellationLocked}
                            className="p-1 text-gray-400 hover:text-emerald-500 transition-colors disabled:opacity-30 disabled:hover:text-gray-400"
                          >
                            <Minus size={12} strokeWidth={3} />
                          </button>
                          <span className={`w-6 text-center text-xs font-black ${item.quantity === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => isEditable && handleUpdateQty(item.id!, 1)}
                            disabled={!isEditable || isLoadingLatestOrder || isManualItem}
                            className="p-1 text-gray-400 hover:text-emerald-500 transition-colors disabled:opacity-30"
                          >
                            <Plus size={12} strokeWidth={3} />
                          </button>
                        </div>
                        {item.quantity > 0 && (
                          <button
                            onClick={() => isEditable && handleRemove(item.id!)}
                            disabled={!isEditable || isLoadingLatestOrder || isManualItem || isCancellationLocked}
                            className="p-1.5 text-red-300 hover:text-red-500 transition-colors disabled:opacity-30"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="px-6 py-8 border-t bg-gray-50">
          <div className="flex justify-between items-center mb-6 px-2">
            <span className="text-gray-500 font-bold text-sm">수정된 예상 금액</span>
            <span className="text-2xl font-black text-emerald-600">{totalPrice.toLocaleString()}원</span>
          </div>
          
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              disabled={isLoadingLatestOrder}
              className="flex-1 h-14 rounded-2xl border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-all active:scale-95"
            >
              닫기
            </button>
            <button 
              onClick={() => isEditable && onSave(localItems)}
              disabled={!isEditable || isLoadingLatestOrder}
              className="flex-[2] h-14 rounded-2xl bg-gray-900 text-white font-black shadow-lg hover:bg-black transition-all active:scale-95 flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:shadow-none disabled:active:scale-100"
            >
              <Save size={18} />
              저장하기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrderBottomSheet;
